'use client';

/* eslint-disable camelcase */
import { animated, useSpring } from '@react-spring/web';
import ReactModal from 'react-modal';
import { useState } from 'react';
import Link from 'next/link';
import mixpanel from 'mixpanel-browser';
import { JobDetails, Spinner } from './views';
import StatusModal from './StatusModal';
import styles from './styles.module.css';

function JobModal({
  isOpen = true, onClose, data, inline = false, initialStatusModalOpen = false,
}) {
  // State for secondary modal
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(initialStatusModalOpen);

  /* Animation Styles */
  const springProps = useSpring({
    opacity: (inline || isOpen) ? 1 : 0,
    transform: `translateY(${(inline || isOpen) ? 0 : 40}px)`,
    onRest: () => {
      if (!inline && !isOpen) {
        // this to run any additional code once the modal closes
      }
    },
  });

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleApplyClick = async () => {
    mixpanel.track('Opportunity Card Applied', {
      'Opportunity ID': data.id,
      'Opportunity Title': data.title,
      'Opportunity Company': data.company,
      'Opportunity Location': data.location,
      'Opportunity Type': data.type,
      'Opportunity Opp Structure': data.opp_structure,
      'Opportunity Industry': data.industry,
    });
  };

  const handleStatusModalOpen = () => {
    setIsStatusModalOpen(true);
  };

  const handleStatusModalClose = () => {
    setIsStatusModalOpen(false);
  };

  const renderContent = () => {
    return <JobDetails data={data} />;
  };

  const renderControls = () => {
    const hasLinks = data.external_links && data.external_links.length > 0;

    return (
      <div className={styles.bottomControls}>
        {hasLinks ? (
          <Link
            href={data?.external_links[0]}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleApplyClick}
            className={styles.button}
          >
            View Application Link
          </Link>
        ) : (
          <div className={styles.disabledButton}>
            No Application Link Provided
          </div>
        )}
        <button
          type="button"
          onClick={handleStatusModalOpen}
          className={styles.statusButton}
        >
          Change Application Status
        </button>
      </div>
    );
  };

  const renderPlaceholder = () => {
    return <Spinner />;
  };

  const mainContent = (
    <animated.div
      className={inline ? styles.inlineMain : styles.main}
      style={{ ...springProps }}
    >
      {data ? renderContent() : renderPlaceholder()}
      {data ? renderControls() : null}
    </animated.div>
  );

  // If inline mode, render without ReactModal wrapper
  if (inline) {
    return (
      <>
        {mainContent}
        <StatusModal
          isOpen={isStatusModalOpen}
          onClose={handleStatusModalClose}
          jobData={data}
        />
      </>
    );
  }

  // Default modal behavior
  return (
    <>
      <ReactModal
        isOpen={isOpen}
        onRequestClose={handleClose}
        className={styles.modal}
        shouldCloseOnEsc
        closeTimeoutMS={400}
        ariaHideApp={false}
      >
        {mainContent}
      </ReactModal>
      <StatusModal
        isOpen={isStatusModalOpen}
        onClose={handleStatusModalClose}
        jobData={data}
      />
    </>
  );
}

export default JobModal;
